import * as React from 'react';
import { styled } from '@linaria/react';

import usePrefersReducedMotion from '@/hooks/use-prefers-reduced-motion';
import useRequestAnimationFrameLoop from '@/hooks/use-request-animation-frame-loop';

import Demo from '@/components/Demo';
import SliderControl from '@/components/Controls/SliderControl';
import Space from '@/components/Space';

import {
  Svg,
  CodeSnippet,
  Row,
} from '../BasicShapesDemo/BasicShapes.shared';

const WIDTH = 280;
const HEIGHT = 340;
const DASH_LENGTH = 0;
const GAP_LENGTH = 26;
const PATH_LENGTH = DASH_LENGTH + GAP_LENGTH;

function CasinoLightsEffect() {
  const [speed, setSpeed] = React.useState(25);
  const [offset, setOffset] = React.useState(0);
  const previousOffset = React.useRef(0);

  const prefersReducedMotion = usePrefersReducedMotion();

  useRequestAnimationFrameLoop(() => {
    previousOffset.current = offset;
    setOffset(offset + speed);
  });

  React.useEffect(() => {
    if (prefersReducedMotion) {
      setSpeed(0);
    }
  }, [prefersReducedMotion]);

  return (
    <Demo
      outputWrapper={OutputWrapper}
      initialValues={{}}
      customGrid={{
        gridTemplateColumns: 'min(400px, 100%)',
        justifyContent: 'center',
      }}
      controls={[
        <SliderControl
          key="speed"
          id="speed"
          label="Animation Speed"
          min={0}
          max={100}
          value={speed}
          setValue={setSpeed}
          handleReset={() => setSpeed(prefersReducedMotion ? 0 : 25)}
        />,
      ]}
    >
      {({}) => {
        const code = `\
@keyframes casinoLights {
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: ${PATH_LENGTH};
  }
}
\n\n
rect {
  stroke-dasharray: ${DASH_LENGTH}, ${GAP_LENGTH};
  animation:
    casinoLights 400ms linear infinite;
}
`;
        return (
          <StyledRow>
            <SvgWrapper>
              <Svg
                width={WIDTH}
                height={HEIGHT}
                viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
              >
                <Shape
                  style={{
                    strokeDashoffset: offset / 100,
                  }}
                  points={`
                    ${WIDTH * 0.2} ${HEIGHT * 0.75}
                    ${WIDTH * 0.5} ${HEIGHT * 0.25}
                    ${WIDTH * 0.8} ${HEIGHT * 0.75}
                  `}
                />
              </Svg>
            </SvgWrapper>

            <StyledCodeSnippet lang="css" code={code} />
          </StyledRow>
        );
      }}
    </Demo>
  );
}

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const StyledRow = styled(Row)`
  @container (max-width: 37rem) {
    flex-direction: column-reverse;
    align-items: center;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  --inline-block-override: 22px;
  --inline-padding-override: 24px;

  @container (max-width: 37rem) {
    width: 100%;
  }
`;

const SvgWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  @container (max-width: 37rem) {
    position: relative;
  }
`;

const Shape = styled.polygon`
  --dash-length: ${DASH_LENGTH}px;
  --gap-length: ${GAP_LENGTH}px;
  --path-length: calc(var(--dash-length) + var(--gap-length));

  width: ${WIDTH / 2}px;
  height: ${WIDTH / 2}px;
  fill: none;
  stroke: var(--wham-complementary-500);
  stroke-width: 10px;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-dasharray: var(--dash-length), var(--gap-length);
`;

export default CasinoLightsEffect;
